// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Field-name alignment for the ADR-004 D6 offline-augment listing (redefined
 * by #35; wired by #27), mirroring how firmware-delivery-meta-response-mapper.ts
 * and au0001-response-mapper.ts already reconcile a raw foreign-AU
 * response_payload onto a widget contract shape.
 *
 * Verified against the real capability contract (2026-07-29):
 *   FirmwareDeliveryAPIv1.ListFirmwarePackageMeta(deviceAnchor, updateMethod,
 *   tsBoundary, tsDirection, limit)
 *   → { success: true, data: FirmwarePackageMeta[] }
 *   | { success: false, error: string }
 * where FirmwarePackageMeta = { method, release, draft_solution, device_model,
 * auto_programming, software_uid, operation_time, operated_by,
 * firmware_package_ref }.
 *
 * ProcessServiceRequest.ds relays this raw response onto response_payload
 * with no renaming. rendezvous.ts's readFileObject unwraps a non-null `data`
 * value one level before this mapper sees it — and because `data` here is an
 * ARRAY (not an object keyed by field name, unlike every other mapper in this
 * widget), that unwrap hands `raw` the FirmwarePackageMeta[] array DIRECTLY,
 * not a wrapping object. A `{ success: false, error }` envelope (empty/failed
 * capability response) survives as-is instead, exactly like the sibling
 * mappers' failure path.
 *
 * Only `release`, `operation_time`, and `operated_by` are surfaced onto the
 * widget's `OfflineBundleRecord` (decided 2026-07-29, alongside confirming
 * this signature) — `method` / `draft_solution` / `device_model` /
 * `auto_programming` / `software_uid` / `firmware_package_ref` are read off
 * the same real payload but intentionally not carried further (ADR-004 D6:
 * this list is compact advisory context, not a second copy of D2's full
 * production/meta layer).
 */

import { toUnknownableString } from "@/contracts/display";
import type { RecentOfflineBundlesResponse } from "@/contracts/requests";

/**
 * Map a raw FirmwareDeliveryAPIv1 `ListFirmwarePackageMeta` response_payload
 * onto RecentOfflineBundlesResponse. Idempotent: a payload that already
 * carries a `bundles` array (e.g. the mock transport's contract-shaped
 * fixtures) passes through unchanged.
 *
 * A missing/malformed/failed listing response maps to `{ bundles: [] }` — an
 * empty advisory list is a valid, non-error result (this stage is advisory
 * context, never part of D3 selection or a D5-style halt), never a silent
 * fabrication of rows that were not actually returned.
 */
export function mapFirmwareDeliveryOfflineBundlesResponse(
  raw: unknown,
): RecentOfflineBundlesResponse {
  // Already-mapped pass-through (e.g. mock transport fixtures).
  if (
    typeof raw === "object" &&
    raw !== null &&
    !Array.isArray(raw) &&
    Array.isArray((raw as Record<string, unknown>)["bundles"])
  ) {
    return raw as unknown as RecentOfflineBundlesResponse;
  }

  // The successful shape: readFileObject already unwrapped `data` down to the
  // raw FirmwarePackageMeta[] array (see this file's header) — `raw` IS that
  // array directly, not `{ data: [...] }`.
  if (Array.isArray(raw)) {
    return {
      bundles: raw.map((entry) => {
        const row =
          typeof entry === "object" && entry !== null
            ? (entry as Record<string, unknown>)
            : {};
        return {
          release: toUnknownableString(row["release"]),
          operationTime: toUnknownableString(row["operation_time"]),
          operatedBy: toUnknownableString(row["operated_by"]),
        };
      }),
    };
  }

  // `{ success: false, error }` (or anything else unrecognized) — no rows,
  // never a thrown/reported failure (this stage is advisory-only).
  return { bundles: [] };
}
